import React, { useEffect, useState } from 'react'
import { useQuery } from '@apollo/client'
import { Patient } from '@babylon/consumer-api/types'
import { useFormatMessage } from '@babylon/intl'
import { ButtonSimple, ButtonVariant } from '@babylon/medkit'
import { Button, Spacer, Spinner } from '@babylon/web-platform-ui'

import { useNextAction } from '@/redux/signup/selectors'
import { useCurrentUser, useTrackingScreenTitle } from '@/hooks'

import messages from './EligibilityPage.messages'
import { Loading, EligibilityForm } from '@/components'
import { FamilyMembersResponse, InsurersResponse } from '@/types'
import FamilyMembersQuery from '@/queries/FamilyMembers'
import InsurersQuery from '@/queries/Insurers'
import { useConsumerNetworkUuid } from '@/redux/userConfig/selectors'
import StepTitle from '@/components/StepHeader/StepTitle'
import { PersonTypes, tealiumEligibilityScreenSections } from '@/constants'
import { trackTealiumEvent } from '@/tracking'
import { useConfig } from '@/redux/partnerConfig/selectors'
import SimpleStepperTitle from '@/components/SimpleStepper/SimpleStepperTitle'

const EligibilityPage = () => {
  const translate = useFormatMessage()
  const nextAction = useNextAction()
  const { userData } = useCurrentUser()
  const stepTrackingName = useTrackingScreenTitle()
  const { useDnaV2Design, steps } = useConfig()
  const consumerNetworkUuid = useConsumerNetworkUuid()
  const [currentPatientIndex, setCurrentPatientIndex] = useState(0)
  const [currentPatient, setCurrentPatient] = useState<Patient>()
  const [patients, setPatients] = useState<Patient[]>([])
  const [pageTitle, setPageTitle] = useState<string>()
  const [content, setContent] = useState<string[]>()
  const [personType, setPersonType] = useState<string>(PersonTypes.FIRST)
  const [userSwitching, setUserSwitching] = useState<boolean>(false)

  const { CHECK_INSURANCE } = tealiumEligibilityScreenSections

  const [formScreenSection, setFormScreenSection] = useState<string>(
    CHECK_INSURANCE
  )

  const {
    data: { familyMembers = [] } = {},
    loading: familyMembersLoading,
  } = useQuery<FamilyMembersResponse>(FamilyMembersQuery)
  const {
    data: { insurers = [] } = {},
    loading: insurersLoading,
  } = useQuery<InsurersResponse>(InsurersQuery, {
    variables: {
      consumerNetworkUuid,
    },
  })

  const intlParams = {
    person: personType,
  }

  useEffect(() => {
    if (patients.length) {
      setCurrentPatient(patients[currentPatientIndex])
    }

    if (
      currentPatientIndex === 0 &&
      steps.some((el) => el.component === 'family')
    ) {
      setPersonType(PersonTypes.FIRST)
    } else {
      setPersonType(PersonTypes.OTHER)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPatientIndex, steps])

  useEffect(() => {
    if (currentPatient) {
      const values = {
        name: currentPatient?.first_name,
        person: personType,
      }

      setPageTitle(translate(messages.pageTitle, values))
      setContent([translate(messages.infoText, values)])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPatient, personType])

  useEffect(() => {
    if (userData) {
      setPatients([userData.patient, ...familyMembers])
    }
  }, [userData, familyMembers])

  useEffect(() => {
    if (patients.length) {
      setCurrentPatient(patients[currentPatientIndex])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patients])

  if (!userData || !currentPatient || familyMembersLoading || insurersLoading) {
    return useDnaV2Design ? <Spinner variant="secondary" /> : <Loading inline />
  }

  const onNextAction = (isSkip?: boolean) => {
    const newPatientIndex = currentPatientIndex + 1

    if (isSkip) {
      trackTealiumEvent({
        screen_title: stepTrackingName,
        eventCategory: 'identity',
        eventAction: 'clicked skip eligibility',
        screenSection: formScreenSection,
        recipientId: currentPatient?.id,
        tealium_event: 'clicked skip eligibility',
      })
    }

    if (newPatientIndex < patients.length) {
      // A little hack to reset the form and simulate user switching
      setUserSwitching(true)
      setTimeout(() => {
        setCurrentPatientIndex(newPatientIndex)
        setUserSwitching(false)
      }, 200)
    } else {
      nextAction()
    }
  }

  const onUpdateScreenSection = (tealiumScreenSection: string) => {
    setFormScreenSection(tealiumScreenSection)
  }

  return (
    <>
      {useDnaV2Design ? (
        <SimpleStepperTitle
          pageTitle={pageTitle}
          content={content}
          noSpaceAfterContent
        />
      ) : (
        <StepTitle pageTitle={pageTitle} content={content} />
      )}

      {userSwitching && <Loading inline />}
      {!userSwitching && (
        <EligibilityForm
          insurers={insurers}
          patient={currentPatient}
          personType={personType}
          onComplete={onNextAction}
          onUpdateScreenSection={onUpdateScreenSection}
        />
      )}
      {useDnaV2Design && (
        <>
          <Button
            onClick={() => onNextAction(true)}
            variant="secondary"
            isFullWidth
            data-testid="eligibility-skip-button"
          >
            {translate(messages.skip, intlParams)}
          </Button>
          <Spacer paddingTop="8" />
        </>
      )}
      {!useDnaV2Design && (
        <ButtonSimple
          onClick={() => onNextAction(true)}
          variant={ButtonVariant.secondary}
          fullWidth
          data-testid="eligibility-skip-button"
        >
          {translate(messages.skip, intlParams)}
        </ButtonSimple>
      )}
    </>
  )
}

export default EligibilityPage
